import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { CircleCheck, CircleSlash, Edit } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GetEventDokumentTypMappings200ResponseDataInner } from '@/adapter/opm-cockpit-backend/__generated';
import { useEventDokumentTypMappings } from '@/adapter/opm-cockpit-backend/useEventDokumentTypMappings';
import { Button } from '@/components/ui/button';
import { CreateDokumentTypBox } from '@/pages/Konfiguration/DokumentTypen/components/CreateDokumentTypBox';
import { getSortIcon } from '@/pages/Vorgaenge/components/TableHeader/SortIcon';

const columnHelper =
  createColumnHelper<GetEventDokumentTypMappings200ResponseDataInner>();

export const DokumentTypenConfig: React.FC = () => {
  const { data, isLoading, error } = useEventDokumentTypMappings();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = [
    columnHelper.accessor('dokumentTyp', {
      header: 'Dokumenttyp',
      cell: (info) => {
        return <span className="text-text-primary">{info.getValue()}</span>;
      },
    }),
    columnHelper.accessor('eventName', {
      header: 'Event Name',
      cell: (info) => {
        return <span className="text-text-primary">{info.getValue()}</span>;
      },
    }),
    columnHelper.accessor('eventTyp', {
      header: 'Event Typ',
      cell: (info) => {
        return <span className="text-text-primary">{info.getValue()}</span>;
      },
    }),
    columnHelper.accessor('aktiviert', {
      header: 'Aktiviert',
      cell: (info) => {
        const deletedAt = info.row.original.deletedAt;

        if (deletedAt) {
          return (
            <div>
              <p>gelöscht</p>
            </div>
          );
        }

        return (
          <div>
            {info.getValue() ? (
              <CircleCheck className="text-[var(--color-status-success)]" />
            ) : (
              <CircleSlash className=" text-[var(--color-status-error)]" />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Aktion',
      cell: ({ row }) => {
        const externalId = row.original.externalId;

        return (
          <>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate(`/konfiguration/dokumenttypen/${externalId}`);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </>
        );
      },
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Lade Dokumenttypen-Konfiguration...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">
          Fehler beim Laden der Dokumenttypen-Konfiguration.
        </p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Keine Dokumenttypen-Konfigurationen gefunden.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CreateDokumentTypBox />
      <div className="bg-white border border-table-border rounded-lg overflow-x-auto overflow-y-auto max-h-[60vh]">
        <table className="w-full">
          {/* Table Header */}
          <thead className="sticky top-0 z-20 bg-table-header border-b border-table-border border-l-4 border-l-table-header">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="text-left p-3 font-medium text-text-primary bg-table-header"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center justify-start ${
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <div className="ml-1 flex items-center">
                            {getSortIcon(header.column.getIsSorted())}
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Table Body */}
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`border-transparent transition-colors border-l-4 hover:border-primary hover:bg-table-hover ${row.original.deletedAt ? 'bg-gray-200' : ''}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="p-3 text-text-primary font-light text-sm"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
